/**
 * A line written by hand.
 *
 * For the things that are not time and not an expense: a fixed fee, a discount,
 * a licence resold at cost. Quantity times rate is computed on the server and
 * shown here as a preview — two places doing the same multiplication is one
 * place too many, but a form that does not show the total it is about to add is
 * a form people distrust.
 *
 * A discount is a **negative rate**, not a separate concept. The server accepts
 * it, the arithmetic holds, and the line says what it is on the document.
 */

import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';
import {
  Alert,
  ApiError,
  Button,
  Input,
  Modal,
  Switch,
  api,
  formatMoney,
  multiplyMoney,
  toast,
} from '@connect/web-shared';

const schema = z.object({
  description: z.string().trim().min(1, 'Say what this is for.').max(255),
  quantity: z
    .string()
    .trim()
    .regex(/^\d+(\.\d{1,3})?$/, 'A quantity, to three decimals.')
    .refine((value) => Number(value) > 0, 'More than nothing.'),
  unit_rate: z
    .string()
    .trim()
    // Leading minus allowed on purpose: that is how a discount is written.
    .regex(/^-?\d+(\.\d{1,2})?$/, 'An amount, to two decimal places.'),
  taxable: z.boolean(),
});

type FormValues = z.infer<typeof schema>;

const DEFAULTS: FormValues = {
  description: '',
  quantity: '1',
  unit_rate: '',
  taxable: true,
};

export function AddLineDialog({
  open,
  onClose,
  onAdded,
  invoicePublicId,
  currency,
}: {
  open: boolean;
  onClose: () => void;
  onAdded?: () => void;
  invoicePublicId: string;
  currency: string;
}) {
  const form = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: DEFAULTS });

  useEffect(() => {
    if (open) form.reset(DEFAULTS);
  }, [open, form]);

  const add = useMutation({
    mutationFn: (values: FormValues) =>
      api.post(`/finance/invoices/${invoicePublicId}/lines`, values),
    onSuccess: () => {
      toast.success('Line added');
      onAdded?.();
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        let placed = false;
        for (const [field, message] of Object.entries(error.fieldErrors)) {
          if (field in DEFAULTS) {
            form.setError(field as keyof FormValues, { message });
            placed = true;
          }
        }
        if (!placed) form.setError('root', { message: error.message });
        return;
      }
      form.setError('root', { message: 'Could not reach the server. Try again.' });
    },
  });

  const quantity = form.watch('quantity');
  const rate = form.watch('unit_rate');
  // A preview only. The server computes and stores the authoritative amount,
  // which is why this is shown as a hint and never sent.
  //
  // `multiplyMoney`, not `(Number(q) * Number(r)).toFixed(2)`. The point of
  // showing it is that nobody is surprised by a rounding they cannot see, and
  // float arithmetic was getting that rounding wrong on ordinary inputs: it
  // previewed 1.005 hours at 1.00 as 1.00 where the invoice says 1.01, because
  // 1.005 has no exact binary form and lands under the half it should round up
  // from. `null` while a box is still being typed into, as before.
  const preview = quantity && rate ? multiplyMoney(quantity, rate) : null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add a line"
      size="md"
      dismissOnBackdrop={false}
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose} disabled={add.isPending}>
            Cancel
          </Button>
          <Button
            size="sm"
            loading={add.isPending}
            onClick={() => void form.handleSubmit((values) => add.mutateAsync(values))()}
          >
            Add line
          </Button>
        </>
      }
    >
      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          void form.handleSubmit((values) => add.mutateAsync(values))();
        }}
      >
        {form.formState.errors.root?.message ? (
          <Alert tone="danger">{form.formState.errors.root.message}</Alert>
        ) : null}

        <Input
          label="Description"
          required
          placeholder="Annual licence — 4 seats"
          error={form.formState.errors.description?.message}
          {...form.register('description')}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Quantity"
            required
            inputMode="decimal"
            error={form.formState.errors.quantity?.message}
            {...form.register('quantity')}
          />
          <Input
            label={`Rate (${currency})`}
            required
            inputMode="decimal"
            placeholder="0.00"
            hint="Negative for a discount."
            error={form.formState.errors.unit_rate?.message}
            {...form.register('unit_rate')}
          />
        </div>

        {preview !== null ? (
          <p className="rounded-control bg-surface-2 px-3 py-2 text-sm text-muted">
            This line will add{' '}
            <span className="font-medium text-body tabular">
              {formatMoney(preview, currency)}
            </span>{' '}
            before tax.
          </p>
        ) : null}

        <Switch
          label="Taxable"
          hint="Off for a rebilled cost that carries no tax of its own."
          {...form.register('taxable')}
        />
      </form>
    </Modal>
  );
}
